import React, { useCallback, useEffect, useState } from "react";
import adminTemplatesService, {
  BuiltinSoapVisibility,
} from "../../../services/adminTemplatesService";

interface Props {
  onClose: () => void;
}

/**
 * Manage the built-in "SOAP Note (Default)" entry per doctor.
 *
 * The built-in note has no DocumentTemplate row, so it cannot be assigned the
 * way custom templates are — it is visible to every doctor until switched off
 * here. Turning it off removes it from that doctor's template switcher, so a
 * doctor who only uses a custom template cannot leave the built-in one
 * selected by accident and generate against the wrong template.
 */
const BuiltinSoapModal: React.FC<Props> = ({ onClose }) => {
  const [rows, setRows] = useState<BuiltinSoapVisibility[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyDoctorId, setBusyDoctorId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setRows(await adminTemplatesService.builtinSoapVisibility());
      setError(null);
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Failed to load doctors");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleToggle = async (row: BuiltinSoapVisibility) => {
    setBusyDoctorId(row.doctor_id);
    setError(null);
    try {
      const updated = await adminTemplatesService.setBuiltinSoapVisibility(
        row.doctor_id,
        !row.enabled,
      );
      setRows((prev) =>
        prev.map((r) =>
          r.doctor_id === updated.doctor_id ? { ...r, enabled: updated.enabled } : r,
        ),
      );
    } catch (err: any) {
      setError(err?.response?.data?.error || err.message || "Could not update");
    } finally {
      setBusyDoctorId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/40 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-900">
                SOAP Note (Default)
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Choose which doctors see the default note in their template
                switcher.
              </p>
            </div>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-gray-400 hover:text-gray-600 text-2xl leading-none"
            >
              ×
            </button>
          </div>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1">
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          {loading ? (
            <div className="py-8 flex justify-center">
              <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-600" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No doctors yet.</p>
          ) : (
            <ul className="divide-y divide-gray-100">
              {rows.map((row) => (
                <li
                  key={row.doctor_id}
                  className="flex items-center justify-between py-3"
                >
                  <span className="text-sm text-gray-800">{row.name}</span>
                  <button
                    onClick={() => handleToggle(row)}
                    disabled={busyDoctorId === row.doctor_id}
                    className={`px-2.5 py-1 rounded-full text-xs font-semibold transition disabled:opacity-50 ${
                      row.enabled
                        ? "bg-green-100 text-green-700 hover:bg-green-200"
                        : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                    }`}
                  >
                    {row.enabled ? "Enabled" : "Disabled"}
                  </button>
                </li>
              ))}
            </ul>
          )}

          <p className="text-xs text-gray-400 mt-4">
            Disabling only hides it from the web switcher. The mobile app does not
            choose a template, so its notes still use the default one.
          </p>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

export default BuiltinSoapModal;
